import React from 'react';
import clsx from 'clsx';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import { Button } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import OverallThresholdInput from 'Components/OverallThresholdInput/OverallThresholdInput';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import useUserConfig from 'Hooks/query-hooks/useUserConfig';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import { Errors, SetFieldValue } from './types';

import styles from './FieldThreshold.css';

type Props = {
    overallThreshold?: number | null;
    globalThreshold: number;
    globalThresholdText: string;
    merchantThresholdText: string | React.ReactNode;
    errors: Errors;
    setFieldValue: SetFieldValue;
    showMerchantThreshold: boolean;
    setShowMerchantThreshold: React.Dispatch<React.SetStateAction<boolean>>;
    setFieldThresholdAction?: React.Dispatch<React.SetStateAction<FieldThresholdAction | undefined>>;
};

const FieldThreshold = ({
    overallThreshold,
    globalThreshold,
    globalThresholdText,
    merchantThresholdText,
    errors,
    setFieldValue,
    showMerchantThreshold,
    setShowMerchantThreshold,
    setFieldThresholdAction
}: Props) => {
    const { t } = useTranslation();
    const thresholdHasError = !!errors.overallThreshold;
    const { getUserInfo } = useAccessTokenStore.getState();
    const uniqueUserTokenId = getUserInfo().tenantKey;
    const userConfig = useUserConfig(uniqueUserTokenId);

    function addMerchantThreshold(add = true) {
        setShowMerchantThreshold(add);
        if (add) {
            setFieldValue('overallThreshold', overallThreshold || globalThreshold);
            if (setFieldThresholdAction) setFieldThresholdAction('add');
        } else {
            setFieldValue('overallThreshold', null);
            if (setFieldThresholdAction) setFieldThresholdAction('remove');
        }
    }

    if (userConfig.isLoading) {
        return <RouteLoading />;
    }

    if (userConfig.isError) {
        const { errorCmp } = errorController([userConfig.error]);
        return errorCmp;
    }

    return (
        <div className={styles.thresholdContainer}>
            <div className={styles.thresholdLabel}>{t('Global Aggregate Scoring Threshold')}</div>
            <div className={clsx(styles.globalThresholdDescription, showMerchantThreshold && styles.disabled)}>
                {globalThresholdText}
            </div>
            {showMerchantThreshold && (
                <div className={styles.conditionalMerchantThresholdContainer}>
                    <label htmlFor='overallThreshold' className={styles.thresholdLabel}>
                        {t('Merchant-Specific Aggregate Scoring Threshold')}
                    </label>
                    <OverallThresholdInput
                        maxGlobalAggregateThreshold={userConfig.data.constants.maxGlobalAggregateThreshold}
                        name='overallThreshold'
                        thresholdText={merchantThresholdText}
                        hasError={thresholdHasError}
                    />
                </div>
            )}
            <div className={styles.thresholdButton}>
                {showMerchantThreshold ? (
                    <Button
                        variant='tertiary'
                        onClick={handleOnClickEvent(() => addMerchantThreshold(false))}
                        onKeyDown={handleKeyEvent(() => addMerchantThreshold(false))}
                        dataTestId='remove-threshold-btn'
                    >
                        {t('Remove Merchant-Specific Aggregate Scoring Threshold')}
                    </Button>
                ) : (
                    <Button
                        variant='contextual-secondary'
                        onClick={handleOnClickEvent(() => addMerchantThreshold())}
                        onKeyDown={handleKeyEvent(() => addMerchantThreshold())}
                        dataTestId='add-threshold-btn'
                    >
                        {t('Add Merchant-Specific Aggregate Scoring Threshold')}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default FieldThreshold;
